/**
 * Weight Freeze (v2) — the shared tone vocabulary → colour + chip sx.
 *
 * `STATUS_TONE` in ../data/reference.js maps a status to a tone NAME
 * (ok / warn / info / neutral / danger / ai). This file maps that tone
 * name to a colour and a ready-made MUI `Chip` sx, so Catalogue, Packing,
 * DWS, Initiate and Analytics all render status chips the same way instead
 * of each hand-rolling its own treatment. Same tone names and colours as
 * the sibling Disputes module's local TONE_COLOR (DisputeGrid.js /
 * DisputeDrawer.js) — kept in sync deliberately, not by coincidence.
 *
 * 'ai' has no MUI severity-palette equivalent (it isn't success/warning/
 * error/info), so it maps to PURPLE.
 */
import {INFO, WARNING, SUCCESS, ERROR, MUTED, PURPLE, withOpacity} from '../../../../styles/foundation/colors'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'

/** Every tone name STATUS_TONE (data/reference.js) can produce. */
export const TONE_COLOR = {
  ok: SUCCESS,
  warn: WARNING,
  info: INFO,
  neutral: MUTED,
  danger: ERROR,
  ai: PURPLE,
}

/** Resolves a tone name to a colour. Unknown tones fall back to MUTED rather
 * than rendering `undefined` into an sx object. */
export function toneColor(tone) {
  return TONE_COLOR[tone] || MUTED
}

/** MUI `Chip` sx for a tone: tinted background, solid text, module type scale. */
export function chipSx(tone) {
  const color = toneColor(tone)
  return {
    bgcolor: withOpacity(color, 0.12),
    color,
    fontFamily: FONT,
    fontWeight: WEIGHT.semibold,
    fontSize: SIZE['2xs'],
    height: 22,
  }
}

/**
 * The app theme title-cases `.MuiChip-label` (theme/overrides/Chip.js), which
 * is right for a one-word status and wrong for everything else: a stored
 * vocabulary value comes out as 'Data Submitted — Awaiting Decision', and CSS
 * `capitalize` breaks on the hyphen too, so 'Sub-category' reads
 * 'Sub-Category'. Any chip whose label is a sentence, a stored enum value or a
 * hyphenated term uses this instead of `chipSx`.
 */
export function chipSentenceSx(tone) {
  return {...chipSx(tone), '& .MuiChip-label': {textTransform: 'none'}}
}
